<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0" />
        </v-col>
        <v-col cols="3">
          <image-background-select />
        </v-col>
      </v-row>
    </v-container>
    <v-container pt-0 pb-10>
      <v-row>
        <v-col>
          <v-img :src="image.fileUrl" :class="[media_imageBackground]" class="grid-background--small mb-6" contain />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div v-for="field in fields" :key="field.key" class="d-flex">
            <v-text-field
              :label="field.key"
              :value="getFormattedValue(get(image, field.value), field.type)"
              :disabled="isEditing && !field.editable"
              :readonly="!field.editable || !isEditing"
              :rules="[v => !!v || 'Field cannot be empty']"
              @input="onInput($event, field)"
            />
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div v-if="isPermittedTo('update')">
            <v-slide-y-reverse-transition mode="out-in" origin="left">
              <div class="">
                <div v-if="!isEditing">
                  <v-btn key="edit" color="primary" depressed @click="isEditing = true">
                    <v-icon small class="mr-2">edit</v-icon>
                    Edit
                  </v-btn>
                  <v-btn key="delete" color="red" dark depressed class="ml-4" @click="deleteModalVisible = true">
                    <v-icon small class="mr-2">delete</v-icon>
                    Delete
                  </v-btn>
                </div>
                <div v-else-if="isEditing" key="save">
                  <v-btn :loading="isLoading" :disabled="isLoading" color="primary" depressed @click="onSaveClick">
                    <v-icon small class="mr-2">save</v-icon>
                    Save
                  </v-btn>
                  <v-btn :disabled="isLoading" class="ml-4" color="error" outlined depressed @click="onDiscardChanges">
                    Discard Changes
                  </v-btn>
                </div>
              </div>
            </v-slide-y-reverse-transition>
          </div>
        </v-col>
      </v-row>
      <delete-modal
        v-if="deleteModalVisible"
        v-model="deleteModalVisible"
        :is-disabled="isLoading"
        :is-loading="isLoading"
        category="Image"
        :item="image.title"
        @modal-confirm="onDeleteConfirm"
      />
    </v-container>
  </page-loader>
</template>

<script>
// Services
import { media } from '@/services/offers'
// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
// Helpers
import { mapMutations, mapState } from 'vuex'
import { clone, get, set } from 'lodash-es'
import { formatDate, formatTime } from '@/utils/date'
import { formatFileSize } from '@/utils/file'
// Components
import PageLoader from '@/components/page-loader'
import ImageBackgroundSelect from '@/components/media-gallery/image-background-select'
import DeleteModal from '@/components/modals/delete-modal'

export default {
  name: 'MediaGalleryView',
  components: { PageLoader, ImageBackgroundSelect, DeleteModal },
  mixins: [PermissionsHandler],
  data: () => ({
    fields: [
      { key: 'Title', value: 'title', editable: true },
      { key: 'URL', value: 'fileUrl' },
      { key: 'Date added', value: 'createdAt', type: 'date' },
      { key: 'File size', value: 'meta.fileSize', type: 'filesize' },
    ],
    image: {},
    isEditing: false,
    isLoading: false,
    updatedImage: {},
    deleteModalVisible: false,
  }),
  computed: {
    ...mapState('media', ['media_imageBackground']),
    breadcrumbs() {
      return [
        { text: 'Media Gallery', to: { name: 'tools.media-gallery' }, exact: true },
        { text: this.image.title || 'loading', disabled: true },
      ]
    },
    canPerformActions() {
      return true
    },
  },
  async created() {
    await this.fetchImage()
    this.setPageLoading(false)
  },
  methods: {
    get,
    ...mapMutations('general', ['setPageLoading']),
    async fetchImage() {
      this.isLoading = true
      try {
        this.image = await media.get(this.$route.params.id)
        this.updatedImage = clone(this.image)
      } catch (e) {
        this.$notify({
          type: 'error',
          text: `Unable to find image with ID: ${this.$route.params.id}`,
        })
        this.$router.push({ name: 'tools.media-gallery' })
      }
      this.isLoading = false
    },
    getFormattedValue(value, type = null) {
      if (!type) return value

      switch (type) {
        case 'date':
          return `${formatDate(value, { format: 'DD month YYYY' })} (${formatTime(value)})`
        case 'filesize':
          return formatFileSize(value)
        default:
          return value
      }
    },
    onDiscardChanges() {
      this.updatedImage = clone(this.image)
      this.isEditing = false
    },
    onInput(value, field) {
      set(this.updatedImage, field.value, value)
    },
    async onSaveClick() {
      if (!this.updatedImage.title.trim()) {
        this.$notify({
          type: 'error',
          text: 'Title cannot be empty',
        })
        return
      } else if (this.updatedImage.title === this.image.title) {
        this.$notify({
          type: 'info',
          text: 'No changes detected',
        })
        return
      }

      await this.updateImage()
      this.isEditing = false
    },
    async updateImage() {
      try {
        this.isLoading = true

        const response = await media.update(this.updatedImage)
        this.image = this.updatedImage
        this.$notify({
          type: 'success',
          text: `Successfully renamed as ${response.title}`,
        })
      } catch (e) {
        this.$notify({
          type: 'error',
          text: `Unable to update image`,
        })
      }
      this.isLoading = false
    },
    async deleteImage() {
      try {
        this.isLoading = true

        media.delete(this.$route.params.id)
        this.$router.push({ name: 'tools.media-gallery' })
        this.$notify({
          type: 'success',
          text: `Deleted ${this.image.title} successfully`,
        })
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete image',
        })
      }
    },
    onDeleteConfirm() {
      this.deleteImage()
      this.deleteModalVisible = false
    },
  },
}
</script>
<style lang="scss">
.list-complete-enter,
.list-complete-leave-to {
  opacity: 0;
  transform: translateY(80px);
}
.list-complete-leave-active {
  position: absolute;
}
</style>
